import { useMemo, useState } from "react";
import {
	Alert,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import GigaVolumeChart, { GIGA_PALETTE } from "./GigaVolumeChart";

export const pct = (value) => {
	if (value == null || Number.isNaN(Number(value))) return "-";
	return `${(Number(value) * 100).toFixed(0)}%`;
};

const GigaExploreView = ({ run }) => {
	const ideas = run?.ideas || [];
	const clusters = run?.clusters || [];
	const [selectedCluster, setSelectedCluster] = useState(clusters[0] || null);
	const [selectedIdea, setSelectedIdea] = useState(null);
	const chartLabels = ideas[0]?.months || [];

	const chartSeries = useMemo(() => {
		if (selectedIdea?.searchVolume) {
			return [{ name: selectedIdea.text, data: selectedIdea.searchVolume }];
		}
		if (selectedCluster) {
			const clusterLine = {
				name: selectedCluster.topic,
				data: selectedCluster.searchVolumeHistory || [],
			};
			const keywordLines = (selectedCluster.keywords || []).slice(0, 5).map((text) => {
				const idea = ideas.find((row) => row.text === text);
				return { name: text, data: idea?.searchVolume || [] };
			});
			return [clusterLine, ...keywordLines];
		}
		if (ideas[0]?.searchVolume) {
			return ideas.slice(0, 5).map((idea) => ({ name: idea.text, data: idea.searchVolume }));
		}
		return [];
	}, [selectedCluster, selectedIdea, ideas]);

	if (!ideas.length) {
		return <Alert severity="info">This run has no keyword data yet.</Alert>;
	}

	return (
		<Stack spacing={2}>
			{chartLabels.length > 0 && chartSeries.length > 0 && (
				<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
					<Typography fontWeight={700} mb={0.5}>
						Search volume
						{selectedCluster && !selectedIdea ? ` · ${selectedCluster.topic}` : ""}
						{selectedIdea ? ` · ${selectedIdea.text}` : ""}
					</Typography>
					<Typography variant="body2" color="text.secondary" mb={1}>
						Click a cluster or keyword row to change the chart.
					</Typography>
					<GigaVolumeChart labels={chartLabels} series={chartSeries} />
				</Paper>
			)}
			{clusters.length > 0 && (
				<Stack direction="row" flexWrap="wrap" gap={1.5}>
					{clusters.map((cluster, index) => {
						const active = selectedCluster?.topic === cluster.topic && !selectedIdea;
						return (
							<Paper
								key={cluster.topic}
								onClick={() => {
									setSelectedCluster(cluster);
									setSelectedIdea(null);
								}}
								variant="outlined"
								sx={{
									p: 1.5,
									minWidth: 240,
									flex: "1 1 240px",
									cursor: "pointer",
									borderColor: active ? GIGA_PALETTE[index % GIGA_PALETTE.length] : "#e5e7eb",
									borderWidth: active ? 2 : 1,
									bgcolor: active ? "#f8fbff" : "white",
								}}>
								<Typography fontWeight={700}>{cluster.topic}</Typography>
								<Typography variant="caption" color="text.secondary">
									Vol {Number(cluster.searchVolume || 0).toLocaleString()} · 3m {pct(cluster.growthThreeMonthsVsAvg)} · YoY{" "}
									{pct(cluster.growthYoY)}
								</Typography>
								<Typography variant="body2" mt={1} color="text.secondary">
									{(cluster.keywords || []).join(", ")}
								</Typography>
							</Paper>
						);
					})}
				</Stack>
			)}
			<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Keyword</TableCell>
							<TableCell align="right">Latest vol</TableCell>
							<TableCell align="right">YoY</TableCell>
							<TableCell align="right">MoM</TableCell>
							<TableCell align="right">3m vs avg</TableCell>
							<TableCell>Competition</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{ideas.map((idea) => (
							<TableRow
								key={idea.text}
								hover
								selected={selectedIdea?.text === idea.text}
								onClick={() => setSelectedIdea(idea)}
								sx={{ cursor: "pointer" }}>
								<TableCell>{idea.text}</TableCell>
								<TableCell align="right">{Number(idea.latestSearchVolume || 0).toLocaleString()}</TableCell>
								<TableCell align="right">{pct(idea.growthYoY)}</TableCell>
								<TableCell align="right">{pct(idea.growthMoM)}</TableCell>
								<TableCell align="right">{pct(idea.growthThreeMonthsVsAvg)}</TableCell>
								<TableCell>{idea.competition || "-"}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);
};

export default GigaExploreView;
